import {CmEdFieldView} from "../interface/cm-ed-field-view.interface";

/**
 * READING the search state the host already computed — never computing it.
 *
 * <h2>These functions do not match anything</h2>
 * The sheet has exactly ONE matcher, in the host, over the ONE fold definition in
 * `util/cm-ed-search-fold.util.ts`. A presentational component that ran its own comparison would be a
 * second matcher, and the header would sooner or later announce a different number of hits than the
 * sheet marks — the precise failure the shared fold util was extracted to prevent.
 *
 * Everything below therefore only ever READS `CmEdFieldView.searchHit`, a flag the host set. No text
 * is folded here, no term is seen here, and no component that calls these knows what was typed.
 *
 * <h2>Why the renderers need them at all</h2>
 * Two rendering decisions are answered by "does this run of fields hold a match":
 * <ul>
 *   <li>a card or a section run whose LEVEL-2 block holds a match opens by itself while the term is
 *       active — a match behind a closed expander is a match the reader is never shown;</li>
 *   <li>a section states how many matches it holds, because nothing dims and the count is then the
 *       only thing distinguishing a section that contains matches from one that does not.</li>
 * </ul>
 * Both are per-COMPONENT questions about arrays the component was handed, and the view models the
 * host hands down carry no per-run aggregate. Aggregating a flag is not matching.
 *
 * Free functions and not a base class: five components need them, they hold no state, and there is
 * nothing to keep in sync.
 */

/**
 * Whether ANY field of a run carries a hit.
 *
 * Short-circuits, so the common answer on a sheet with a rare term costs one pass and stops at the
 * first true. Total in its input: a `null` run has no hits rather than throwing, because this is read
 * during change detection on view models that are rebuilt while the reader types.
 */
export function cmEdHasHit(fields: readonly CmEdFieldView[] | null | undefined): boolean {

    if (!fields) {
        return false;
    }

    for (const field of fields) {

        if (field?.searchHit) {
            return true;
        }
    }

    return false;
}

/**
 * How many fields of a run carry a hit.
 *
 * Counted over the fields the caller actually RENDERS, never over fields it merely knows about: a
 * count that included level-3 fields a card or a section does not show would state hits the reader
 * cannot reach on this view, which is exactly the under-/over-reporting the sheet's honesty rule
 * forbids. Which array is reachable is the caller's knowledge, so the decision stays at the call site
 * and this function only counts what it is given.
 */
export function cmEdCountHits(fields: readonly CmEdFieldView[] | null | undefined): number {

    if (!fields) {
        return 0;
    }

    let hits = 0;

    for (const field of fields) {

        if (field?.searchHit) {
            hits++;
        }
    }

    return hits;
}
